import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { PageHeader, Card, Button, Alert } from '../components/ui';
import accountService from '../services/accountService';
import accountAppAuthService from '../services/accountAppAuthService';
import { ArrowLeftIcon, ArrowPathIcon, KeyIcon, XCircleIcon } from '@heroicons/react/24/outline';

const AccountNewManual = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    apiid: '',
    apihash: '',
    phonenumber: '',
    type: 'monitoring',
    leave_existing_groups: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [authSessionId, setAuthSessionId] = useState('');
  const [authStatus, setAuthStatus] = useState('');
  const [authSessionError, setAuthSessionError] = useState('');
  const [passwordHint, setPasswordHint] = useState('');
  const [selectedProxyId, setSelectedProxyId] = useState('');
  const [proxyForm, setProxyForm] = useState({ label: '', proxyUrl: '' });
  const [proxyCheckResult, setProxyCheckResult] = useState('');
  const consumeTriggeredRef = useRef(false);

  // Create account mutation
  const createAccountMutation = useMutation(
    (accountData) => accountService.createAccount(accountData),
    {
      onSuccess: (data) => {
        setSuccess('Account created successfully');
        // Redirect to account details after a short delay
        setTimeout(() => {
          navigate(`/accounts/${data.id}`);
        }, 2000);
      },
      onError: (err) => {
        console.error('Error creating account:', err);
        setError(`Failed to create account: ${err.response?.data?.message || err.message}`);
      }
    }
  );

  const handleAuthSessionPayload = (data) => {
    if (!data) {
      return;
    }

    setAuthStatus(data.status || '');
    setPasswordHint(data.password_hint || '');
    setAuthSessionError(data.error || '');
  };

  useQuery(
    ['account-app-auth-session', authSessionId],
    () => accountAppAuthService.getSession(authSessionId),
    {
      enabled: Boolean(authSessionId) && !consumeTriggeredRef.current,
      refetchInterval: 2500,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        handleAuthSessionPayload(data);

        if (data?.status === 'credentials_ready' && authSessionId && !consumeTriggeredRef.current) {
          consumeTriggeredRef.current = true;
          consumeSessionMutation.mutate(authSessionId);
        }
      },
      onError: (err) => {
        setAuthSessionError(err.response?.data?.message || err.message);
      }
    }
  );

  const {
    data: appAuthProxies = [],
    refetch: refetchAppAuthProxies,
  } = useQuery(
    ['account-app-auth-proxies'],
    () => accountAppAuthService.listProxies(),
    {
      onError: (err) => {
        setAuthSessionError(err.response?.data?.message || err.message);
      }
    }
  );

  const startAppAuthMutation = useMutation(
    () => accountAppAuthService.startSession(formData.phonenumber, selectedProxyId),
    {
      onSuccess: (data) => {
        consumeTriggeredRef.current = false;
        setVerificationCode('');
        setPassword('');
        setAuthSessionId(data.sessionId);
        handleAuthSessionPayload(data);
        setSuccess('Telegram app authorization started. Enter the code from Telegram.');
      },
      onError: (err) => {
        setAuthSessionError(err.response?.data?.message || err.message);
      }
    }
  );

  const createProxyMutation = useMutation(
    () => accountAppAuthService.createProxy(proxyForm),
    {
      onSuccess: (proxy) => {
        setProxyForm({ label: '', proxyUrl: '' });
        setSelectedProxyId(proxy.id);
        refetchAppAuthProxies();
        setSuccess('Proxy saved for Telegram app authorization.');
      },
      onError: (err) => {
        setAuthSessionError(err.response?.data?.message || err.message);
      }
    }
  );

  const deleteProxyMutation = useMutation(
    (proxyId) => accountAppAuthService.deleteProxy(proxyId),
    {
      onSuccess: (_, proxyId) => {
        if (selectedProxyId === proxyId) {
          setSelectedProxyId('');
        }
        refetchAppAuthProxies();
        setSuccess('Proxy removed from Telegram app authorization.');
      },
      onError: (err) => {
        setAuthSessionError(err.response?.data?.message || err.message);
      }
    }
  );

  const checkProxyMutation = useMutation(
    (proxyId) => accountAppAuthService.checkProxy(proxyId),
    {
      onSuccess: (data) => {
        const diagnostics = data?.diagnostics || {};
        const observedIp = diagnostics.observedIp || 'unknown';
        const proxyServer = diagnostics.server || data?.proxy_url_masked || 'selected proxy';
        const fingerprint = diagnostics.browserFingerprint?.label
          ? ` using ${diagnostics.browserFingerprint.label} (${diagnostics.browserFingerprint.locale}, ${diagnostics.browserFingerprint.timezoneId})`
          : '';
        setProxyCheckResult(`${data.label || 'Proxy'} routes Chromium as ${observedIp} via ${proxyServer}${fingerprint}`);
        setAuthSessionError(diagnostics.error || '');
      },
      onError: (err) => {
        setProxyCheckResult('');
        setAuthSessionError(err.response?.data?.message || err.message);
      }
    }
  );

  const submitCodeMutation = useMutation(
    () => accountAppAuthService.submitCode(authSessionId, verificationCode),
    {
      onSuccess: (data) => {
        handleAuthSessionPayload(data);
        setSuccess(data.status === 'waiting_for_password'
          ? 'Password required. Enter Telegram password.'
          : 'Code accepted. Fetching app credentials...');
      },
      onError: (err) => {
        setAuthSessionError(err.response?.data?.message || err.message);
      }
    }
  );

  const submitPasswordMutation = useMutation(
    () => accountAppAuthService.submitPassword(authSessionId, password),
    {
      onSuccess: (data) => {
        handleAuthSessionPayload(data);
        setSuccess('Password accepted. Fetching app credentials...');
      },
      onError: (err) => {
        setAuthSessionError(err.response?.data?.message || err.message);
      }
    }
  );

  const cancelSessionMutation = useMutation(
    () => accountAppAuthService.cancelSession(authSessionId),
    {
      onSuccess: () => {
        consumeTriggeredRef.current = false;
        setAuthSessionId('');
        setAuthStatus('failed');
        setPassword('');
        setVerificationCode('');
        setAuthSessionError('Cancelled by operator');
      },
      onError: (err) => {
        setAuthSessionError(err.response?.data?.message || err.message);
      }
    }
  );

  const consumeSessionMutation = useMutation(
    (sessionId) => accountAppAuthService.consumeSession(sessionId),
    {
      onSuccess: (data) => {
        const phoneNumber = data.phonenumber || formData.phonenumber;
        const phoneDigits = String(phoneNumber || '').replace(/\D/g, '');

        setFormData((current) => ({
          ...current,
          name: data.name || phoneDigits || current.name,
          phonenumber: phoneNumber || current.phonenumber,
          apiid: data.apiid || current.apiid,
          apihash: data.apihash || current.apihash,
        }));
        setAuthStatus(data.status || 'consumed');
        setAuthSessionError('');
        setAuthSessionId('');
        setVerificationCode('');
        setPassword('');
        setSuccess('API credentials imported into the account form.');
      },
      onError: (err) => {
        consumeTriggeredRef.current = false;
        setAuthSessionError(err.response?.data?.message || err.message);
      }
    }
  );

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((current) => {
      if (name !== 'phonenumber') {
        return {
          ...current,
          [name]: value
        };
      }

      const digits = value.replace(/\D/g, '');
      const shouldMirrorName = !current.name || current.name === current.phonenumber.replace(/\D/g, '');

      return {
        ...current,
        phonenumber: value,
        name: shouldMirrorName ? digits : current.name,
      };
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form data
    if (!formData.name || !formData.apiid || !formData.apihash || !formData.phonenumber) {
      setError('All fields are required');
      return;
    }

    // Create account
    createAccountMutation.mutate(formData);
  };

  const handleStartCredentialFlow = () => {
    setError('');
    setSuccess('');
    setAuthSessionError('');

    if (!formData.phonenumber.trim()) {
      setError('Phone number is required before starting Telegram app authorization');
      return;
    }

    startAppAuthMutation.mutate();
  };

  const handleSubmitCode = (e) => {
    e.preventDefault();
    setAuthSessionError('');
    submitCodeMutation.mutate();
  };

  const handleSubmitPassword = (e) => {
    e.preventDefault();
    setAuthSessionError('');
    submitPasswordMutation.mutate();
  };

  const handleCreateProxy = (e) => {
    e.preventDefault();
    setAuthSessionError('');

    if (!proxyForm.label.trim() || !proxyForm.proxyUrl.trim()) {
      setAuthSessionError('Proxy label and URL are required');
      return;
    }

    createProxyMutation.mutate();
  };

  useEffect(() => {
    if (!authSessionId) {
      consumeTriggeredRef.current = false;
    }
  }, [authSessionId]);

  const authStatusLabel = {
    starting: 'Opening my.telegram.org',
    waiting_for_code: 'Waiting for Telegram code',
    waiting_for_password: 'Waiting for Telegram password',
    resolving_app: 'Resolving or creating Telegram app',
    credentials_ready: 'Credentials ready',
    consumed: 'Credentials imported',
    failed: 'Auth flow failed',
    expired: 'Session expired',
  }[authStatus] || 'Not started';

  return (
    <div>
      <PageHeader
        title="Add New Account"
        description="Create a new Telegram account and fetch app credentials from my.telegram.org"
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeftIcon}
            onClick={() => navigate('/accounts')}
          >
            Back
          </Button>
        }
      />

      {error && (
        <Alert
          variant="error"
          className="mb-4"
          isDismissible
          onDismiss={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          variant="success"
          className="mb-4"
          isDismissible
          onDismiss={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}

      <Card title="Telegram App API Wizard" className="mb-6">
        <div className="space-y-6">
          <div>
            <label htmlFor="wizard-phone" className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="phonenumber"
                id="wizard-phone"
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="e.g., +12345678901"
                value={formData.phonenumber}
                onChange={handleChange}
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              The worker enters the Telegram code in this wizard. If the site blocks automation, you can still fill API ID and API Hash manually below.
            </p>
          </div>

          <div className="border rounded-md p-4 bg-gray-50 space-y-4">
            <div className="border-b border-gray-200 pb-4 space-y-4">
              <div>
                <label htmlFor="app-auth-proxy" className="block text-sm font-medium text-gray-700">
                  Proxy for API App Creation
                </label>
                <select
                  id="app-auth-proxy"
                  className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  value={selectedProxyId}
                  onChange={(e) => setSelectedProxyId(e.target.value)}
                  disabled={Boolean(authSessionId)}
                >
                  <option value="">No proxy (server IP)</option>
                  {appAuthProxies.map((proxy) => (
                    <option key={proxy.id} value={proxy.id}>
                      {proxy.label} — {proxy.proxy_url_masked}
                    </option>
                  ))}
                </select>
	                <p className="mt-1 text-sm text-gray-500">
	                  Used only for my.telegram.org app API authorization. TDLib account sessions are not proxied here.
	                </p>
	                {proxyCheckResult && (
	                  <p className="mt-2 text-sm text-green-700">
	                    {proxyCheckResult}
	                  </p>
	                )}
	              </div>

              {appAuthProxies.length > 0 && (
                <div className="space-y-2">
                  {appAuthProxies.map((proxy) => (
                    <div key={proxy.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm">
                      <div>
                        <div className="font-medium text-gray-900">{proxy.label}</div>
                        <div className="text-gray-500">{proxy.proxy_url_masked}</div>
                        {proxy.last_used_at && (
                          <div className="text-xs text-gray-400">
                            Last used: {new Date(proxy.last_used_at).toLocaleString()}
                          </div>
                        )}
                      </div>
	                      <div className="flex items-center gap-2">
	                        <Button
	                          type="button"
	                          variant="secondary"
	                          size="sm"
	                          onClick={() => checkProxyMutation.mutate(proxy.id)}
	                          isLoading={checkProxyMutation.isLoading}
	                        >
	                          Test
	                        </Button>
	                        <Button
	                          type="button"
	                          variant="danger"
	                          size="sm"
	                          onClick={() => deleteProxyMutation.mutate(proxy.id)}
	                          isLoading={deleteProxyMutation.isLoading}
	                        >
	                          Remove
	                        </Button>
	                      </div>
	                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={handleCreateProxy} className="grid grid-cols-1 gap-3 md:grid-cols-5">
                <input
                  type="text"
                  className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md md:col-span-1"
                  placeholder="Proxy label"
                  value={proxyForm.label}
                  onChange={(e) => setProxyForm((current) => ({ ...current, label: e.target.value }))}
                />
                <input
                  type="text"
                  className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md md:col-span-3"
                  placeholder="http://user:pass@host:port or socks5://host:port"
                  value={proxyForm.proxyUrl}
                  onChange={(e) => setProxyForm((current) => ({ ...current, proxyUrl: e.target.value }))}
                />
                <Button
                  type="submit"
                  variant="secondary"
                  isLoading={createProxyMutation.isLoading}
                  disabled={createProxyMutation.isLoading}
                >
                  Add Proxy
                </Button>
              </form>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="primary"
                icon={KeyIcon}
                onClick={handleStartCredentialFlow}
                isLoading={startAppAuthMutation.isLoading}
                disabled={startAppAuthMutation.isLoading || submitCodeMutation.isLoading || submitPasswordMutation.isLoading}
              >
                Get API ID & Hash
              </Button>
              <Button
                type="button"
                variant="secondary"
                icon={XCircleIcon}
                onClick={() => cancelSessionMutation.mutate()}
                isLoading={cancelSessionMutation.isLoading}
                disabled={!authSessionId || cancelSessionMutation.isLoading}
              >
                Cancel Flow
              </Button>
              <Button
                type="button"
                variant="secondary"
                icon={ArrowPathIcon}
                onClick={handleStartCredentialFlow}
                disabled={!formData.phonenumber || startAppAuthMutation.isLoading}
              >
                Restart
              </Button>
            </div>

            <div className="text-sm text-gray-700">
              <span className="font-medium">Status:</span> {authStatusLabel}
            </div>

            {authSessionError && (
              <Alert variant="warning" isDismissible onDismiss={() => setAuthSessionError('')}>
                {authSessionError}
              </Alert>
            )}

            {authStatus === 'waiting_for_code' && (
              <form onSubmit={handleSubmitCode} className="space-y-3">
                <div>
                  <label htmlFor="verification-code" className="block text-sm font-medium text-gray-700">
                    Telegram Code
                  </label>
                  <input
                    type="text"
                    id="verification-code"
                    className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter the code sent by Telegram"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={submitCodeMutation.isLoading}
                  disabled={submitCodeMutation.isLoading || !verificationCode.trim()}
                >
                  Submit Code
                </Button>
              </form>
            )}

            {authStatus === 'waiting_for_password' && (
              <form onSubmit={handleSubmitPassword} className="space-y-3">
                <div>
                  <label htmlFor="telegram-password" className="block text-sm font-medium text-gray-700">
                    Telegram Password
                  </label>
                  {passwordHint && (
                    <p className="text-sm text-gray-500">Hint: {passwordHint}</p>
                  )}
                  <input
                    type="password"
                    id="telegram-password"
                    className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter Telegram cloud password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={submitPasswordMutation.isLoading}
                  disabled={submitPasswordMutation.isLoading || !password}
                >
                  Submit Password
                </Button>
              </form>
            )}

            {(formData.apiid || formData.apihash) && (
              <div className="text-sm text-green-700">
                Credentials are in the form below. Review them and create the account.
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card title="Account Information">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Account Name
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="name"
                id="name"
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="e.g., tdata1"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Defaults to the phone number without "+"
            </p>
          </div>

          <div>
            <label htmlFor="apiid" className="block text-sm font-medium text-gray-700">
              API ID
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="apiid"
                id="apiid"
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="e.g., 12345678"
              value={formData.apiid}
              onChange={handleChange}
              required
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Imported from the wizard above or entered manually
            </p>
          </div>

          <div>
            <label htmlFor="apihash" className="block text-sm font-medium text-gray-700">
              API Hash
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="apihash"
                id="apihash"
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="e.g., 1a2b3c4d5e6f7g8h9i0j"
              value={formData.apihash}
              onChange={handleChange}
              required
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Imported from the wizard above or entered manually
            </p>
          </div>

          <div>
            <label htmlFor="phonenumber" className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="phonenumber"
                id="phonenumber"
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="e.g., +12345678901"
              value={formData.phonenumber}
              onChange={handleChange}
                required
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Phone number with country code (e.g., +1 for US)
            </p>
          </div>

          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700">
              Тип аккаунта
            </label>
            <div className="mt-1">
              <select
                name="type"
                id="type"
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                value={formData.type}
                onChange={handleChange}
              >
                <option value="monitoring">Observer (loads history)</option>
                <option value="ai">AI</option>
                <option value="agent">Agent (sends messages)</option>
              </select>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Observer accounts load Messenger history. Agent accounts can send messages.
            </p>
          </div>

          {formData.type === 'agent' && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
              <label htmlFor="leave_existing_groups" className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="leave_existing_groups"
                  id="leave_existing_groups"
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  checked={formData.leave_existing_groups}
                  onChange={(e) => setFormData((current) => ({
                    ...current,
                    leave_existing_groups: e.target.checked,
                  }))}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-700">
                    Leave all existing groups and channels
                  </span>
                  <span className="mt-1 block text-sm text-gray-500">
                    Runs once after the first successful authorization. Private chats are never removed.
                  </span>
                </span>
              </label>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/accounts')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={createAccountMutation.isLoading}
              disabled={createAccountMutation.isLoading}
            >
              Create Account
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AccountNewManual;
